import {useCallback} from "react";
import {useSelector} from "react-redux";
import {RootState} from "../redux/store";
import {client, ICustomConfig} from "./api-client";
import {API_BASE_URL_V2} from "../services/config";

/**
 * Optional endpoint-group name. Historically selected between v1/v2 base
 * URLs via a per-feature flag; any truthy value now resolves to the v2
 * base URL, and the string itself is ignored.
 *
 * WARNING: passing no value falls through to `API_BASE_URL`, which is the
 * DECOMMISSIONED v1 API — not a "non-versioned" base, as this comment
 * previously claimed. A bare `useClient()` is therefore a live bug. The
 * remaining bare callers are in `messages.ts`, which has no v2 equivalent
 * and is knowingly out of scope. Once those are resolved, delete this
 * parameter so the hook cannot silently route to v1 again.
 */
const useClient = (feature?: string) => {
  const {userInfo} = useSelector((state: RootState) => state.userLogin);
  const token = userInfo?.accessToken;
  const useV2 = Boolean(feature);

  return useCallback(
    (endpoint: string, config?: Partial<ICustomConfig>) =>
      client(
        endpoint,
        {
          ...config,
          ...("token" in (config ?? {}) ? {} : {token}),
        },
        useV2 ? API_BASE_URL_V2 : undefined
      ),
    [token, useV2]
  );
};

const useClientV2 = () => {
  const {userInfo} = useSelector((state: RootState) => state.userLogin);
  const token = userInfo?.accessToken;
  return useCallback(
    (endpoint: string, config?: Partial<ICustomConfig>) =>
      client(
        endpoint,
        {
          ...config,
          ...("token" in (config ?? {}) ? {} : {token}),
        },
        API_BASE_URL_V2
      ),
    [token]
  );
};

export {useClient, useClientV2};
